/**
 * Key-based data hook for ThreatIntelDrawer
 *
 * KEY-BASED PATTERN: Fetches vulnerability data internally using threatIntelKey,
 * eliminating race conditions from dual state sources. The URL param
 * (threatIntelDrawerKey) is the single source of truth.
 *
 * @param threatIntelKey - The vulnerability key to fetch data for (from URL param threatIntelDrawerKey)
 * @returns Vulnerability data, loading state, and refetch function
 */

import { useMy } from '@/hooks/useMy';
import { Vulnerability } from '@/types';

export function useThreatIntelDrawerData(threatIntelKey: string) {
  // Fetch vulnerability data by key (single source of truth)
  const {
    data: vulnerabilityData = [],
    status,
    refetch,
  } = useMy(
    {
      query: {
        node: {
          labels: ['Vulnerability'],
          filters: [
            {
              field: 'key',
              operator: '=',
              value: threatIntelKey || '',
            },
          ],
        },
      },
      resource: 'vulnerability' as const,
      global: true, // Vulnerabilities are global
    },
    {
      enabled: Boolean(threatIntelKey),
      staleTime: 30000, // 30 seconds - vulnerability data doesn't change frequently
      gcTime: 300000, // 5 minutes cache retention
    }
  );

  // Extract vulnerability from response
  const vulnerability = vulnerabilityData?.[0] as Vulnerability | undefined;

  return {
    vulnerability,
    status,
    refetch,
  };
}
